<script lang="ts">
	import { formatPrice } from "../../../../core/utils/formatPrice.js";
	import { getGlobalSettings } from "../../getGlobalSettings.js";
	import StaticPicture from "../Picture/StaticPicture.svelte";
	import Icon from "../Icon.svelte";
	import trash from "../../../../core/assets/icons/trash.svg?raw";
	import type { Empty } from "../../../../core/utils/Empty.js";
	import type { TooltipImage } from "./ProductConfiguratorOptionProps.js";
	import Link from "../Link.svelte";
	import { nbspify } from "../../../../core/utils/nbspify.js";
	import type { OpenableOption } from "./OpenableOption.js";
	import { frequentlyAskedQuestionsPathMap } from "../../../../core/schema/paths/frequentlyAskedQuestionsPathMap.js";
	import { ProductOption } from "../../../../core/schema/ProductOption.js";

	export let id: string;
	export let name: string;
	export let minimalPricesForOptions: Record<ProductOption, number>;
	export let pricesForOptions: Record<ProductOption, number>;
	export let minimalPriceIsFinal = false;
	export let image: string | undefined = undefined;
	export let enabled = false;
	export let icon: string;
	export let tooltipCharacter: string | undefined = "?";
	export let cancelChanges: () => void = () => true;
	export let productOption: OpenableOption;
	export let openedOption: OpenableOption | undefined = undefined;
	export let tooltipPosition: "top" | "bottom" = "top";
	/**
	 * `tooltipImages` are currently not used. Kept for future possible use.
	 */
	export const tooltipImages: TooltipImage[] = [];

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	interface $$Slots {
		tooltip: Empty;
		drawer: Empty;
	}

	const { TENANT_LOCALE } = getGlobalSettings();

	function disable(event: Event): void {
		event.preventDefault();
		enabled = false;
		cancelChanges();
	}

	let input: HTMLInputElement | null = null;
	$: {
		if (input) {
			if (input.checked !== enabled) {
				input.checked = enabled;
				input.dispatchEvent(new Event("change", { bubbles: true }));
				input.dispatchEvent(new Event("input", { bubbles: true }));
			}
		}
	}

	$: price =
		productOption !== "photos"
			? pricesForOptions[productOption]
			: pricesForOptions[ProductOption.StandingPhotos] + pricesForOptions[ProductOption.LyingPhoto];
	$: minimalPrice =
		productOption !== "photos"
			? minimalPricesForOptions[productOption]
			: Math.min(
					minimalPricesForOptions[ProductOption.StandingPhotos],
					minimalPricesForOptions[ProductOption.LyingPhoto],
				);
	// eslint-disable-next-line no-warning-comments
	// FIXME remove this after upgrade to Svelte 5.
	/* eslint-disable @typescript-eslint/explicit-function-return-type */
</script>

<div class="relative flex rounded-lg">
	<label
		class="flex w-full flex-col items-center justify-start gap-3 rounded-lg {enabled
			? 'border-secondary border-2 px-2 py-3 sm:p-3'
			: 'border-grey-200 border px-[calc(.5rem+1px)] py-[calc(.75rem+1px)] sm:p-[calc(.75rem+1px)]'}"
		for={id}
	>
		<div class="flex w-full cursor-pointer items-center gap-2">
			<input
				bind:this={input}
				type="checkbox"
				class="hidden"
				{id}
				name={id}
				bind:checked={enabled}
				readonly={enabled}
				on:click={(event) => {
					openedOption = productOption;
					if (enabled || $$slots.drawer) {
						event.preventDefault();
					}
				}}
			/>
			<div class="relative">
				<Icon {icon} class="aspect-square h-5 sm:h-7" />
			</div>
			<div class="flex gap-2">
				{#if image}
					<StaticPicture {image} alt={name} width={32} height={32} class="aspect-square w-8" />
				{/if}
				{#if tooltipPosition === "top"}
					<div
						class="group/tooltip relative pr-4 text-xs leading-[1.2] transition-all duration-200 after:pointer-events-none after:absolute after:-left-12 after:bottom-full after:h-4 after:w-full after:min-w-[18rem] after:content-[''] hover:after:pointer-events-auto sm:text-base"
						class:font-semibold={enabled}
					>
						<span>{name}</span>
						{#if $$slots.tooltip}
							<span
								class="bg-grey-200 absolute right-0 top-0 ml-0.5 grid aspect-square h-3 place-items-center rounded-full text-[9px] font-bold"
							>
								{tooltipCharacter}
							</span>

							<div
								role="none"
								class="border-1 border-secondary-250 bg-secondary-100 after:border-t-secondary-100 before:border-t-secondary-250 absolute -left-10 bottom-full z-50 mb-3 hidden min-w-[15rem] cursor-default rounded-lg px-5 pb-[1.375rem] pt-4 text-xs before:absolute before:left-3 before:top-full before:border-l-[calc(.45rem+1.5px)] before:border-r-[calc(.45rem+1.5px)] before:border-t-[calc(.75rem+1.5px)] before:border-transparent before:content-[''] after:absolute after:left-[calc(.75rem+1px)] after:top-full after:border-l-[.45rem] after:border-r-[.45rem] after:border-t-[.75rem] after:border-transparent after:content-[''] group-hover/tooltip:flex sm:-left-12 sm:mb-[.875rem] sm:min-w-[18rem] sm:before:border-l-[calc(.45rem+1px)] sm:before:border-r-[calc(.45rem+1px)] sm:before:border-t-[calc(.75rem+1px)]"
								on:click={(event) => {
									if (!(event.target instanceof HTMLAnchorElement)) {
										event.preventDefault();
									}
								}}
							>
								<div class="flex flex-col gap-[.875rem] !font-normal">
									<slot name="tooltip" />
									<p>
										Více informací v sekci
										<Link href="/{frequentlyAskedQuestionsPathMap[TENANT_LOCALE]}">časté dotazy</Link>.
									</p>
								</div>
							</div>
						{/if}
					</div>
				{:else}
					<div
						class="group/tooltip relative pr-4 text-xs leading-[1.2] transition-all duration-200 after:pointer-events-none after:absolute after:-left-12 after:top-full after:h-4 after:w-full after:min-w-[18rem] after:content-[''] hover:after:pointer-events-auto sm:text-base"
						class:font-semibold={enabled}
					>
						<span>{name}</span>
						{#if $$slots.tooltip}
							<span
								class="bg-grey-200 absolute right-0 top-0 ml-0.5 grid aspect-square h-3 place-items-center rounded-full text-[9px] font-bold"
							>
								{tooltipCharacter}
							</span>

							<div
								role="none"
								class="border-1 border-secondary-250 bg-secondary-100 after:border-b-secondary-100 before:border-b-secondary-250 absolute -left-10 top-full z-50 mt-3 hidden min-w-[15rem] cursor-default rounded-lg px-5 pb-4 pt-[1.375rem] text-xs before:absolute before:bottom-full before:left-3 before:border-b-[calc(.75rem+1.5px)] before:border-l-[calc(.45rem+1.5px)] before:border-r-[calc(.45rem+1.5px)] before:border-transparent before:content-[''] after:absolute after:bottom-full after:left-[calc(.75rem+1px)] after:border-b-[.75rem] after:border-l-[.45rem] after:border-r-[.45rem] after:border-transparent after:content-[''] group-hover/tooltip:flex sm:-left-12 sm:mt-[.875rem] sm:min-w-[18rem] sm:before:border-b-[calc(.75rem+1px)] sm:before:border-l-[calc(.45rem+1px)] sm:before:border-r-[calc(.45rem+1px)]"
								on:click={(event) => {
									if (!(event.target instanceof HTMLAnchorElement)) {
										event.preventDefault();
									}
								}}
							>
								<div class="flex flex-col gap-[.875rem] !font-normal">
									<slot name="tooltip" />
									<p>
										Více informací v sekci
										<Link href="/{frequentlyAskedQuestionsPathMap[TENANT_LOCALE]}">časté dotazy</Link>.
									</p>
								</div>
							</div>
						{/if}
					</div>
				{/if}
			</div>
			<div class="flex flex-1 gap-2 sm:gap-1">
				<span
					class="flex flex-1 justify-end text-xs transition-all duration-200 sm:text-base"
					class:font-semibold={enabled}
				>
					{#if (!enabled || price === 0) && !minimalPriceIsFinal}
						od&nbsp;{nbspify(formatPrice(minimalPrice, TENANT_LOCALE, false))}
					{:else if minimalPriceIsFinal && price === 0}
						+&nbsp;{nbspify(formatPrice(minimalPrice, TENANT_LOCALE, false))}
					{:else}
						+&nbsp;{nbspify(formatPrice(price, TENANT_LOCALE, false))}
					{/if}
				</span>
				{#if enabled}
					<button type="button" on:click={disable}>
						<Icon
							icon={trash}
							class="hover:text-secondary-300 text-secondary-250 aspect-square h-4 transition-colors duration-300"
						/>
					</button>
				{/if}
			</div>
		</div>
	</label>
</div>

<slot name="drawer" />
